import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * One `?<name>=<id>` deep-linkable sheet/panel param — the deal board's
 * `?deal=` and the lead queue's `?lead=` are the same pattern, so it lives
 * here once. `open`/`close` are built on the FUNCTIONAL `setSearchParams`
 * form, so they never close over a stale params snapshot and stay
 * referentially stable across renders — safe to hand straight to memoised
 * children (no `openXRef` indirection needed at the call site).
 *
 * The param drives RE-opening too: `location.key` re-applies it when
 * navigation targets the SAME url while a different id (opened from
 * within the page) is on screen — e.g. a second notification/link for the
 * same param while the page is already mounted.
 */
export function useSheetParam(name: string): {
  id: string | null;
  open: (id: string) => void;
  close: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  const [id, setId] = useState<string | null>(null);

  const paramId = searchParams.get(name);
  useEffect(() => {
    if (paramId) setId(paramId);
  }, [paramId, locationKey]);

  const open = useCallback(
    (nextId: string) => {
      setId(nextId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(name, nextId);
          return next;
        },
        { replace: true },
      );
    },
    [name, setSearchParams],
  );

  const close = useCallback(() => {
    setId(null);
    // Drop the param so a refresh/back does not reopen the sheet; `replace`
    // keeps the deep-linked entry out of the history stack.
    setSearchParams(
      (prev) => {
        if (!prev.has(name)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(name);
        return next;
      },
      { replace: true },
    );
  }, [name, setSearchParams]);

  return { id, open, close };
}
